/**
 * multiSiteDashboard — the reads behind the ALL SITES dashboard.
 *
 * The All Sites dashboard is the same shared card board a site's dashboard is, fed the whole
 * estate's figures instead of one site's. Everything here fans one per-site call out across the
 * sites that are online and folds the answers into the single shape the board already reads:
 * counts add up, time series add bucket by bucket, lists merge newest first.
 *
 * ── Ids are SITE-QUALIFIED ─────────────────────────────────────────────────────────────────
 * Exactly as on the All Sites alert board (multiSiteHexAlerts): two sites are two databases,
 * so every camera, event, group and employee id crossing this module becomes
 * `nodeId::id`. That keeps two sites' "camera 1" apart on the board, and it is the routing
 * information for the way back — a record, a picture or a link opened from a row is fetched
 * down the tunnel of the site its id names (`splitSiteCameraKey`).
 *
 * ── A site that fails is left out, not fatal ───────────────────────────────────────────────
 * One unreachable site must not blank the estate's board. Only when EVERY site fails does a
 * read fail, so the board's "couldn't read" banner means what it says.
 */
import type {
  AiAnalyticsSummary,
  AttendanceApi,
  ModelApi,
  Model,
  WatchlistApi,
  WatchlistDetection,
} from '@trinetra/shared';
import * as attendance from '../services/attendance.service';
import {
  getNodeModels,
  getNodeReportSurfaces,
  getNodeWatchlistDetections,
  getNodeWatchlistSummary,
} from '../services/api.service';
import { siteCameraKey as qualify, splitSiteCameraKey, type SiteRef } from './multiSiteHexAlerts.adapter';

export type { SiteRef };

/** Split a qualified id; a bare id has no site. */
export const splitSiteKey = splitSiteCameraKey;

/** The answers of the sites that answered, each with its site. */
export interface SiteAnswers<T> {
  ok: Array<{ site: SiteRef; value: T }>;
  failed: SiteRef[];
}

/**
 * Ask every site, in parallel. Rejects only when every site failed (with the first error);
 * an empty site list answers nothing rather than failing.
 */
export async function fanOut<T>(sites: SiteRef[], ask: (site: SiteRef) => Promise<T>): Promise<SiteAnswers<T>> {
  const settled = await Promise.allSettled(sites.map((s) => ask(s)));
  const out: SiteAnswers<T> = { ok: [], failed: [] };
  let firstError: unknown = null;
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') out.ok.push({ site: sites[i], value: r.value });
    else {
      out.failed.push(sites[i]);
      firstError ??= r.reason;
    }
  });
  if (sites.length > 0 && out.ok.length === 0) throw firstError;
  return out;
}

/** The node proxy answers with a bare array, `{data}` or `{data, pagination}`. */
export function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

const q = (nodeId: string, id: any) => (id === null || id === undefined || id === '' ? id : qualify(nodeId, String(id)));

/** An event row with its ids qualified, and its site remembered. */
export function qualifyEvent<T extends Record<string, any>>(nodeId: string, row: T): T {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    ...('id' in row ? { id: q(nodeId, row.id) } : {}),
    ...('event_id' in row ? { event_id: q(nodeId, row.event_id) } : {}),
    ...('camera_id' in row ? { camera_id: q(nodeId, row.camera_id) } : {}),
  };
}

/** The same row as its own site knows it — what a per-site record body must be handed. */
export function rawEvent<T extends Record<string, any>>(row: T): T {
  if (!row || typeof row !== 'object') return row;
  const bare = (v: any) => (typeof v === 'string' ? splitSiteKey(v).cameraId : v);
  return {
    ...row,
    ...('id' in row ? { id: bare(row.id) } : {}),
    ...('event_id' in row ? { event_id: bare(row.event_id) } : {}),
    ...('camera_id' in row ? { camera_id: bare(row.camera_id) } : {}),
  };
}

/** Epoch seconds from whatever a row carries — seconds, ms or ISO. */
export function epochOf(v: any): number {
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n > 1e12 ? n / 1000 : n;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t / 1000 : 0;
}

const rowTime = (r: any) => epochOf(r?.event_timestamp ?? r?.timestamp ?? r?.created_at);

/** Merge per-site lists newest first, capped. */
export function mergeNewest<T>(lists: T[][], limit?: number): T[] {
  const all = lists.flat().sort((a, b) => rowTime(b) - rowTime(a));
  return limit ? all.slice(0, limit) : all;
}

// ─── Summary folding ─────────────────────────────────────────────────────────

interface SeriesLike { start: number; step: number; values: number[] }

const isSeries = (v: any): v is SeriesLike =>
  !!v && typeof v === 'object' && Array.isArray(v.values) && typeof v.step === 'number';

/**
 * Two sites' series as one. Sites share a clock and the node decides the buckets, so the steps
 * match; the starts can differ by a bucket when a window boundary falls between two requests,
 * so values are placed by time against the first series rather than by index.
 */
function addSeries(a: SeriesLike, b: SeriesLike): SeriesLike {
  if (a.step !== b.step || !a.step) return a.values.length >= b.values.length ? a : b;
  const values = [...a.values];
  b.values.forEach((v, i) => {
    const at = Math.round((b.start + i * b.step - a.start) / a.step);
    if (at >= 0 && at < values.length) values[at] = (Number(values[at]) || 0) + (Number(v) || 0);
  });
  return { ...a, values };
}

/** Keys whose value is ONE record — the estate's newest is the newer of the two. */
const NEWEST_KEYS = new Set(['latest', 'latestAlert', 'latestCritical']);

/**
 * Fold one site's figures into another's: numbers add, series add, lists concatenate,
 * a "latest" record is the newer one, a window spans both, and labels stay as they are.
 */
function fold(a: any, b: any, key = ''): any {
  if (a === null || a === undefined) return b;
  if (b === null || b === undefined) return a;
  if (typeof a === 'number' && typeof b === 'number') {
    if (key === 'oldest' || key === 'from') return Math.min(a, b);
    if (key === 'to' || key === 'peak') return Math.max(a, b);
    return a + b;
  }
  if (isSeries(a) && isSeries(b)) return addSeries(a, b);
  if (Array.isArray(a) && Array.isArray(b)) {
    // A watch list's per-tag split: one row per tag, so the same tag from two sites is one row.
    if (key === 'by_category') {
      const byTag = new Map<string, any>();
      [...a, ...b].forEach((row) => {
        const tag = String(row?.category ?? '');
        byTag.set(tag, byTag.has(tag) ? fold(byTag.get(tag), row) : row);
      });
      return [...byTag.values()];
    }
    return [...a, ...b];
  }
  if (typeof a === 'object' && typeof b === 'object') {
    if (NEWEST_KEYS.has(key)) return epochOf(b.event_timestamp) > epochOf(a.event_timestamp) ? b : a;
    const out: Record<string, any> = { ...a };
    Object.keys(b).forEach((k) => { out[k] = fold(a[k], b[k], k); });
    // A series' peak is the merged series' tallest bucket, not either site's.
    if (isSeries(out.series) && typeof out.peak === 'number') out.peak = Math.max(0, ...out.series.values);
    return out;
  }
  return a;
}

/** Qualify every camera id in a site's figures (and the employees its arrivals name). */
function qualifyFigures(nodeId: string, v: any, key = ''): any {
  if (Array.isArray(v)) return v.map((x) => qualifyFigures(nodeId, x, key));
  if (!v || typeof v !== 'object' || isSeries(v)) return v;
  const out: Record<string, any> = {};
  Object.entries(v).forEach(([k, x]) => {
    if (k === 'camera_id' || (k === 'id' && key === 'arrivals')) out[k] = q(nodeId, x);
    else out[k] = qualifyFigures(nodeId, x, k);
  });
  return out;
}

/** Every answering site's `/analytics/summary` as the estate's. */
export function foldSummaries(answers: SiteAnswers<AiAnalyticsSummary>): AiAnalyticsSummary {
  const sites = answers.ok.map(({ site, value }) => qualifyFigures(site.nodeId, value));
  const merged = sites.reduce((acc, s) => fold(acc, s), null as any);
  // `cached` is per node; the estate's figures are only as cached as all of them.
  return { ...merged, cached: answers.ok.every((a) => a.value?.cached) };
}

/**
 * A model's `/stats` across sites. The person stats are `[by sub-type, by camera, total]`; the
 * per-camera list is the one the board draws, so it is re-ranked across the estate.
 */
export function foldStats(answers: SiteAnswers<any>): any {
  const sites = answers.ok.map(({ site, value }) => qualifyFigures(site.nodeId, value));
  if (sites.every((s) => Array.isArray(s) && s.some(Array.isArray))) {
    const width = Math.max(...sites.map((s) => s.length));
    return Array.from({ length: width }, (_, i) => {
      const parts = sites.map((s) => s[i]).filter((p) => p !== undefined);
      if (parts.every(Array.isArray)) {
        const rows = parts.flat();
        return rows.some((r: any) => r?.camera_id !== undefined)
          ? rows.sort((x: any, y: any) => Number(y.count || 0) - Number(x.count || 0)).slice(0, 20)
          : rows;
      }
      return parts.reduce((acc, p) => fold(acc, p), null as any);
    });
  }
  return sites.reduce((acc, s) => fold(acc, s), null as any);
}

// ─── The All Sites slices of the shared api ──────────────────────────────────

const ONE_SITE_ONLY = () => Promise.reject(new Error('Select a site first — this is only available inside one site.'));

/**
 * Watch-list sightings across the estate. Only the reads the dashboard makes are fanned out;
 * managing the list is a site's business.
 */
export function createMultiSiteWatchlistApi(sites: () => SiteRef[]): WatchlistApi {
  return {
    people: ONE_SITE_ONLY,
    searchRegistry: ONE_SITE_ONLY,
    flagPerson: ONE_SITE_ONLY,
    unflagPerson: ONE_SITE_ONLY,
    async detections(params = {}) {
      const res = await fanOut(sites(), (s) => getNodeWatchlistDetections(s.nodeId, params as Record<string, any>));
      const lists = res.ok.map(({ site, value }) =>
        toArray(value).map((d: WatchlistDetection) => qualifyEvent(site.nodeId, d)));
      return { data: mergeNewest(lists, params.limit) };
    },
    async detectionSummary(params = {}) {
      const res = await fanOut(sites(), (s) => getNodeWatchlistSummary(s.nodeId, params as Record<string, any>));
      return res.ok.reduce(
        (acc, { value }) => ({
          person: acc.person + (Number(value?.person) || 0),
          vehicle: acc.vehicle + (Number(value?.vehicle) || 0),
          total: acc.total + (Number(value?.total) || 0),
        }),
        { person: 0, vehicle: 0, total: 0 },
      );
    },
  };
}

/**
 * The model registry across the estate — every site's models in one list, which is what makes
 * the board draw a model's card when ANY site runs it.
 */
export function createMultiSiteModelApi(sites: () => SiteRef[]): ModelApi {
  return {
    async list(params) {
      const res = await fanOut(sites(), (s) => getNodeModels(s.nodeId, params || {}));
      return { data: res.ok.flatMap(({ value }) => toArray(value)) as Model[], meta: null };
    },
    async surfacesWithData() {
      const res = await fanOut(sites(), (s) => getNodeReportSurfaces(s.nodeId));
      return [...new Set(res.ok.flatMap(({ value }) => value || []))];
    },
    create: ONE_SITE_ONLY,
    update: ONE_SITE_ONLY,
    delete: ONE_SITE_ONLY,
    attachToCamera: ONE_SITE_ONLY,
    listCameras: async () => [],
  };
}

/**
 * Presence across the estate, and one employee's record read from their own site.
 *
 * A presence row's employee id is qualified, which routes their record. The record then asks
 * for pictures by the ids IT holds, which are its site's bare ids — those go to the site whose
 * employee was opened last, which is the record on screen.
 */
export function createMultiSiteAttendanceApi(sites: () => SiteRef[]): AttendanceApi {
  let lastSite: string | null = null;
  /**
   * Site and bare id for an id the board handed out — or the record on screen's site. Only an
   * employee read (`record`) moves that site: the presence list loads its thumbnails, from
   * every site, while a record is open.
   */
  const route = (id: string, record = false) => {
    const { nodeId, cameraId: bare } = splitSiteKey(String(id));
    if (nodeId && record) lastSite = nodeId;
    const site = nodeId || lastSite;
    if (!site) throw new Error('Select a site first');
    return { site, id: bare };
  };

  const slices: Partial<AttendanceApi> = {
    async today(date) {
      const res = await fanOut(sites(), (s) => attendance.getAttendanceToday(s.nodeId, date));
      const parts = res.ok.map(({ site, value }) => ({
        ...value,
        employees: toArray(value?.employees).map((e: any) => ({
          ...e,
          id: q(site.nodeId, e.id),
          primary_photo_id: q(site.nodeId, e.primary_photo_id),
        })),
      }));
      return parts.reduce((acc, p) => fold(acc, p), null as any) || { employees: [] };
    },
    getEmployee: async (id) => { const r = route(id, true); return attendance.getEmployee(r.site, r.id); },
    employeeDay: async (id, date) => { const r = route(id, true); return attendance.getEmployeeDay(r.site, r.id, date); },
    employeeSightings: async (id, params) => { const r = route(id, true); return attendance.getEmployeeSightings(r.site, r.id, params); },
    employeeMonthly: async (id, month) => { const r = route(id, true); return attendance.getEmployeeMonthly(r.site, r.id, month); },
    employeePhotoDataUrl: async (photoId) => { const r = route(photoId); return attendance.getEmployeePhotoDataUrl(r.site, r.id); },
    eventImageDataUrl: async (eventId, type) => { const r = route(eventId); return attendance.getEventImageDataUrl(r.site, r.id, type); },
    listCameras: async () => [],
  };

  // Everything else — configuration, payroll, enrolment — belongs inside one site.
  return new Proxy(slices, {
    get: (target, prop) => (prop in target ? (target as any)[prop] : ONE_SITE_ONLY),
  }) as AttendanceApi;
}
